import React, { useMemo, useState, useRef, useCallback } from 'react';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';
import NavigationItem from './navigation-item';
import style from './navigation.module.scss';
import IMenuItem from '../../../models/header/IMenuItem';
import useOnClickOutside from '../../../hooks/use-on-click-outside.hook';

export type Props = {
  menuItems: IMenuItem[];
  pathname: string;
  themeColor?: string;
  themeCopyColor?: string;
  invertedTheme?: boolean;
  brandPrimaryColor?: string;
};

export default function Navigation({
  menuItems,
  pathname,
  themeColor,
  themeCopyColor,
  invertedTheme,
  brandPrimaryColor,
}: Props) {
  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const navigationRef = useRef<HTMLDivElement>(null);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.Large), 100);

  const toggleMenu = useCallback((id: string, isExpanded: boolean, parentId: string = '') => {
    setOpenMenus((prev) => {
      const updated = new Set<string>();
      if (!parentId) {
        if (!isExpanded) updated.add(id);
      } else {
        const siblingPrefix = `${parentId}-`;
        for (const menuId of prev) {
          if (!menuId.startsWith(siblingPrefix)) updated.add(menuId);
        }
        if (!isExpanded) updated.add(id);
      }
      return updated;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, id: string, parentId: string = '') => {
      const target = e.currentTarget;
      const menuItems = navigationRef.current?.querySelectorAll<HTMLButtonElement>(
        `[data-parent-id="${parentId}"]`,
      );

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const isExpanded = openMenus.has(id);
        toggleMenu(id, isExpanded, parentId);
        if (!isExpanded) {
          const firstChild = navigationRef.current?.querySelector<HTMLButtonElement>(
            `[data-parent-id="${id}"]`,
          );
          firstChild?.focus();
        }
      } else if (e.key === 'Escape') {
        setOpenMenus((prev) => {
          const updated = new Set(prev);
          updated.delete(id);
          return updated;
        });
        const parentButton = navigationRef.current?.querySelector<HTMLButtonElement>(
          `[data-id="${parentId}"]`,
        );
        parentButton?.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (menuItems) {
          const currentIndex = Array.from(menuItems).indexOf(target);
          const nextItem = menuItems[currentIndex + 1] || menuItems[0];
          nextItem?.focus();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (menuItems) {
          const currentIndex = Array.from(menuItems).indexOf(target);
          const prevItem = menuItems[currentIndex - 1] || menuItems[menuItems.length - 1];
          prevItem?.focus();
        }
      } else if (e.key === 'ArrowRight' && !openMenus.has(id)) {
        e.preventDefault();
        toggleMenu(id, false, parentId);
        const firstChild = navigationRef.current?.querySelector<HTMLButtonElement>(
          `[data-parent-id="${id}"]`,
        );
        firstChild?.focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const parentButton = navigationRef.current?.querySelector<HTMLButtonElement>(
          `[data-id="${parentId}"]`,
        );
        if (parentButton) {
          setOpenMenus((prev) => {
            const updated = new Set(prev);
            updated.delete(id);
            return updated;
          });
          parentButton.focus();
        }
      }
    },
    [openMenus, toggleMenu],
  );

  const closeAllMenus = useCallback(() => {
    setOpenMenus(new Set());
  }, []);

  // Using hook to close sub-menus when clicking outside
  useOnClickOutside(navigationRef, closeAllMenus);

  const styles: React.CSSProperties = useMemo(() => {
    return {
      '--navigation-color': themeColor,
      '--navigation-copy-color': themeCopyColor,
      '--brand-primary-color': brandPrimaryColor,
    } as React.CSSProperties;
  }, [themeColor, themeCopyColor, brandPrimaryColor]);

  if (!isBreakpointUp) {
    return null;
  }

  return (
    <div
      ref={navigationRef}
      className={`${style.navigation} navigation d-none d-lg-block ${invertedTheme ? style['inverted-theme'] : ''}`}
      style={styles}
    >
      <div className='container'>
        <nav className={style['navigation__nav']} aria-label='Main Navigation'>
          <NavigationItem
            items={menuItems}
            level={0}
            parentId=''
            openMenus={openMenus}
            pathname={pathname}
            toggleMenu={toggleMenu}
            handleKeyDown={handleKeyDown}
            closeAllMenus={closeAllMenus}
          />
        </nav>
      </div>
    </div>
  );
}
